import { useEffect, useMemo, useRef, useState } from "react";
import { CheckIcon, ChevronDownIcon } from "lucide-react";

import { Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { useGetCandidaciesMinimal } from "@/hooks/queries/useGetCandidaciesMinimal";
import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import { useGetUserSettings } from "@/hooks/queries/useGetUserSettings";
import { ALL_USERS_KEY } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { DashboardSelectedUser } from "@/types";

export const StafferHeaderSelect = ({
  onChange,
  skipSelfDefault = false,
  value,
}: {
  onChange: (next: DashboardSelectedUser) => void;
  // True when the caller already seeded the view (e.g. a dashboard deep link).
  skipSelfDefault?: boolean;
  value: DashboardSelectedUser;
}) => {
  /* Database CRUD Operations */
  // Read
  const { data: activeCandidacies = [], isPending: isCandidaciesPending } =
    useGetCandidaciesMinimal();
  // Observe the expanded cache without fetching hidden candidates for the header.
  const { data: allCandidacies } = useGetCandidaciesMinimal(true, false);
  const candidacies = allCandidacies ?? activeCandidacies;
  const { data: userSettings } = useGetUserSettings();
  const currentUser = useGetCurrentUser();

  /* React Hooks */
  // useContext
  const { userNameMap } = useTaggableUsers();

  // useState
  const [open, setOpen] = useState(false);

  // useMemo — staffers who own at least one candidacy on this job.
  const stafferOptions = useMemo<{ userId: number; name: string }[]>(() => {
    const ids = new Set<number>();
    for (const c of candidacies) {
      for (const owner of c.owners) ids.add(owner.userId);
    }
    return Array.from(ids)
      .map((userId) => ({ userId, name: userNameMap[userId] ?? "Unknown" }))
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }, [candidacies, userNameMap]);

  // useEffect — default to the current user if they own candidacies here,
  // unless they've opted out via user settings. Bypasses mixpanel since
  // it isn't a user action.
  const didDefaultStafferRef = useRef(false);
  useEffect(() => {
    if (
      didDefaultStafferRef.current ||
      skipSelfDefault ||
      userSettings?.pipelineFilterOnSelfDefault === false ||
      isCandidaciesPending ||
      !currentUser
    )
      return;
    didDefaultStafferRef.current = true;
    if (
      value === ALL_USERS_KEY &&
      stafferOptions.some((o) => o.userId === currentUser.id)
    ) {
      onChange(currentUser.id);
    }
  }, [
    isCandidaciesPending,
    currentUser,
    skipSelfDefault,
    stafferOptions,
    userSettings,
    value,
    onChange,
  ]);

  const firstName = (name: string) => name.split(" ")[0];
  const selectedLabel =
    value === ALL_USERS_KEY
      ? "All staffers"
      : firstName(
          stafferOptions.find((o) => o.userId === value)?.name ?? "Unknown",
        );

  const handleSelect = (next: DashboardSelectedUser) => {
    mixpanelUtils.trackFilterAndSortOptionSelected(
      MIXPANEL_SCREENS.JOB,
      "Staffer",
      String(next),
      userSettings?.pipelineViewPreference
        ? { pipeline_view: userSettings.pipelineViewPreference }
        : {},
    );
    onChange(next);
    setOpen(false);
  };

  /* Render */
  const renderOption = (optionValue: DashboardSelectedUser, label: string) => (
    <DropdownMenuItem
      className="justify-between gap-4"
      key={String(optionValue)}
      onSelect={() => {
        handleSelect(optionValue);
      }}
    >
      <Text className="text-xs font-medium text-slate-700">{label}</Text>
      {optionValue === value && (
        <CheckIcon className="h-4 w-4 text-primary-600" />
      )}
    </DropdownMenuItem>
  );

  const render = () => (
    <Row className="items-center gap-1.5">
      <Text className="text-xs font-normal text-slate-500">
        Showing candidates for:
      </Text>
      <DropdownMenu onOpenChange={setOpen} open={open}>
        <DropdownMenuTrigger asChild>
          <Button
            className="h-7 gap-1 px-2 text-xs font-medium text-slate-900 hover:bg-slate-100"
            disabled={isCandidaciesPending || stafferOptions.length === 0}
            size="sm"
            variant="ghost"
          >
            <Text className="text-xs font-medium text-slate-900">
              {selectedLabel}
            </Text>
            <ChevronDownIcon className="h-3.5 w-3.5 text-slate-500" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          {renderOption(ALL_USERS_KEY, "All staffers")}
          {stafferOptions.map((option) =>
            renderOption(option.userId, option.name),
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </Row>
  );

  return render();
};
